import React from 'react';
import { Link } from 'react-router-dom';
import { DocPage, Note, Steps, WqTable, Crumbs, DASHBOARD_URL } from '../../components/documentation/web-integration';

const CONTENTS = [
    'How settlement works',
    'Before you start',
    'Add a settlement account',
    'Choose when you get settled',
    'From payment to your bank',
    'Track your settlements',
    'Settlement statuses',
    'Change your settlement account',
    'Common questions',
];

const SETTINGS_PATH = ['Settings', 'WayaQuick Settings'];

const DASHBOARD_LOGIN = (
    <>
        Log in to your merchant dashboard at{' '}
        <a href={DASHBOARD_URL} target="_blank" rel="noreferrer" className="wq-link">
            app.wayaquick.com
        </a>
        .
    </>
);

const OVERVIEW_STEPS = [
    <>
        <strong>Add a settlement account</strong> — the bank account your money is paid into.
    </>,
    <>
        <strong>Choose when you get settled</strong> — instantly, daily (T+1) or weekly.
    </>,
    <>
        <strong>Collect payments</strong> from your customers through payment links, invoices, your website or your
        app.
    </>,
    <>
        <strong>Get settled.</strong> WayaQuick takes its fee off each payment and pays the rest into your
        settlement account on the schedule you chose.
    </>,
];

const SETTLEMENT_OPTIONS = [
    [
        <strong>Instant</strong>,
        'A WayaBank account',
        'As each payment comes in',
        'You want your money straight away',
    ],
    [
        <strong>Daily (T+1)</strong>,
        'Any other Nigerian bank account',
        'The day after the payment',
        'You want your money every day in another bank',
    ],
    [
        <strong>Weekly</strong>,
        'Any other Nigerian bank account',
        'Once a week, for the week’s payments',
        'You prefer one larger payment each week',
    ],
];

const ADD_ACCOUNT_STEPS = [
    DASHBOARD_LOGIN,
    <>
        Click <strong>Settings</strong> in the side menu, then <strong>WayaQuick Settings</strong>.
    </>,
    <>
        Open the <strong>Settlement Account</strong> tab and click <strong>Add Account</strong>.
    </>,
    <>
        In <strong>Select Bank</strong>, choose your bank. For instant settlement, choose{' '}
        <strong>WAYA MFB</strong> (WayaBank).
    </>,
    <>
        Type your 10-digit <strong>Account number</strong>. The <strong>Account name</strong> fills in by itself
        once WayaQuick has checked the account with your bank. If no name appears, check the bank and the number.
    </>,
    <>
        Choose a <strong>Branch/Category</strong>, such as your main branch. If you haven't created one yet, click{' '}
        <strong>Add Branch</strong> at the top of the form first.
    </>,
    <>
        Tick <strong>Set as Primary Account?</strong> if this should be the account you're settled into. You can
        have only one primary account.
    </>,
    <>
        Click <strong>Add bank account</strong>. The account now appears in your list of settlement accounts.
    </>,
];

const CHOOSE_SCHEDULE_STEPS = [
    <>
        In <strong>WayaQuick Settings</strong>, open the <strong>Payment Preferences</strong> tab.
    </>,
    <>
        Scroll to <strong>Settlement</strong> and choose <strong>Instant</strong> or <strong>Other banks</strong>.
    </>,
    <>
        <strong>For Instant</strong>, pick your account under <strong>WayaBank account</strong>.{' '}
        <strong>For Other banks</strong>, pick your account under <strong>Settlement bank account</strong>, then
        choose <strong>DAILY (T+1)</strong> or <strong>WEEKLY</strong> under <strong>Settlement schedule</strong>.
    </>,
    <>
        Click <strong>Save changes</strong>. The account you picked becomes your default settlement account, and
        every settlement from then on follows this choice.
    </>,
];

const PAYMENT_TO_BANK = [
    <>
        <strong>Your customer pays</strong> on the WayaQuick checkout — by dynamic virtual account, bank transfer,
        card, USSD or PayAttitude — through a payment link, an invoice, your website or your app.
    </>,
    <>
        <strong>WayaQuick confirms the payment.</strong> It appears under <strong>Transactions</strong> in your
        dashboard. The <strong>Settlement Status</strong> column shows whether that payment has been settled to you
        yet.
    </>,
    <>
        <strong>The payment is added to your balance</strong>, after WayaQuick's fee is taken off. You can see it
        under <strong>Current Balance</strong> on the Settlements page.
    </>,
    <>
        <strong>WayaQuick creates a settlement</strong> for the payment, with a due date based on your schedule.
        Until it's paid, its status is <strong>Pending</strong>.
    </>,
    <>
        <strong>The money is paid into your default settlement account</strong> on the due date — straight away for
        Instant, the next day for Daily (T+1), or at the end of the week for Weekly. The status changes to{' '}
        <strong>Settled</strong>.
    </>,
];

const FEE_EXAMPLE = [
    ['Customer pays', '₦50,000.00'],
    ['WayaQuick fee (1.5%)', '− ₦750.00'],
    [<strong>Settled to your account</strong>, <strong>₦49,250.00</strong>],
];

const STAT_CARDS = [
    [<strong>Last Settlement</strong>, 'The amount of your most recent settlement.'],
    [<strong>Next Settlement</strong>, 'The amount due in your next settlement.'],
    [<strong>Transaction Count</strong>, 'How many settlements are settled and how many are still pending.'],
    [
        <strong>Current Balance</strong>,
        <>
            Money collected that hasn't been settled yet. Click <strong>View History</strong> to see how your balance
            has changed.
        </>,
    ],
];

const LIST_COLUMNS = [
    [<strong>Reference ID</strong>, 'The settlement’s reference. Quote it if you contact support.'],
    [<strong>Beneficiary</strong>, 'Your business name.'],
    [<strong>Actual Amount</strong>, 'What your customers paid, before fees.'],
    [<strong>Settled Amount</strong>, 'What was paid into your account, after fees.'],
    [<strong>Settled Account Name</strong>, 'The name on the account the money went to.'],
    [<strong>Settlement Due Date</strong>, 'When the settlement is scheduled, based on your settlement option.'],
    [<strong>Settled Date</strong>, 'When the money was actually sent.'],
    [<strong>Status</strong>, 'Where the settlement is — see the statuses below.'],
];

const STATUSES = [
    [<strong>Pending</strong>, 'Waiting for its due date. Nothing to do.'],
    [<strong>Settled</strong>, 'Paid into your settlement account.'],
    [
        <strong>Failed</strong>,
        'The transfer to your bank account didn’t go through. Check the account details, then contact support with the Reference ID.',
    ],
    [<strong>Refunded</strong>, 'The money went back to the customer, so it isn’t settled to you.'],
];

const CHANGE_ACCOUNT_STEPS = [
    <>
        <strong>From Payment Preferences</strong> (recommended): pick a different account or settlement option in
        the <strong>Settlement</strong> section and click <strong>Save changes</strong>.
    </>,
    <>
        <strong>From the Settlement Account tab:</strong> click <strong>View More</strong> on the account, switch
        on <strong>Set account as default</strong>, choose when to be settled, and click{' '}
        <strong>Update bank account</strong>.
    </>,
];

function Settlement() {
    return (
        <DocPage
            seo={{
                title: 'Settlement',
                description:
                    'How WayaQuick settles your money: add a settlement account, choose instant, daily (T+1) or weekly settlement, and track every settlement from your merchant dashboard.',
            }}
            eyebrow="Get paid · Settlement"
            title={
                <>
                    WayaQuick — <em>Settlement</em>
                </>
            }
            contents={CONTENTS}
            footer="WayaQuick · Settlement · Add your account, choose your schedule, and track every settlement from your merchant dashboard."
            prev={{ to: '/payment-link', label: 'WayaQuick Payment Links' }}
            next={{ to: '/dev-get-started', label: 'Getting Started' }}
        >
            <section className="wq-section" id={CONTENTS[0]}>
                <h2 className="wq-h2">How settlement works</h2>
                <p className="wq-lead">
                    Settlement is how the money your customers pay reaches your bank account. WayaQuick collects each
                    payment, takes its fee off, and pays the rest into your settlement account — instantly, daily
                    (T+1) or weekly, whichever you choose.
                </p>
                <Steps items={OVERVIEW_STEPS} />
            </section>

            <section className="wq-section" id={CONTENTS[1]}>
                <h2 className="wq-h2">Before you start</h2>
                <ul className="wq-p wq-list">
                    <li>
                        <strong>Only live payments are settled.</strong> Test mode uses test money, so nothing is paid
                        into your bank. Your KYC must be approved and your dashboard switched to Live mode —{' '}
                        <Link to="/test-environment" className="wq-link">
                            see Going live
                        </Link>
                        .
                    </li>
                    {/* <li>
                        <strong>Use a business account in your business's name.</strong> The account name is checked
                        with your bank when you add it.
                    </li> */}
                    <li>
                        <strong>Team members need the right access.</strong> The Settlement Account tab and the
                        Payment Preferences tab only appear for roles allowed to manage settlement accounts and
                        payouts. If you can't see them, ask the business owner.
                    </li>
                </ul>
            </section>

            <section className="wq-section" id={CONTENTS[2]}>
                <h2 className="wq-h2">Add a settlement account</h2>
                <p className="wq-p">Find it in your dashboard at:</p>
                <Crumbs items={[...SETTINGS_PATH, 'Settlement Account', 'Add Account']} />
                <Steps items={ADD_ACCOUNT_STEPS} />
                <Note label="Want instant settlement?">
                    Instant settlement only goes to a WayaBank account, so add your WayaBank (WAYA MFB) account here
                    first. You can add more than one settlement account — for example a WayaBank account and an
                    account at another bank — and switch between them later.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[3]}>
                <h2 className="wq-h2">Choose when you get settled</h2>
                <p className="wq-p">There are three settlement options. Pick the one that suits your cash flow.</p>
                <WqTable
                    columns={['Option', 'Paid into', 'When you’re paid', 'Good for']}
                    rows={SETTLEMENT_OPTIONS}
                />

                <h4 className="wq-h4">Set your settlement option</h4>
                <p className="wq-p">Find it in your dashboard at:</p>
                <Crumbs items={[...SETTINGS_PATH, 'Payment Preferences', 'Settlement']} />
                <Steps items={CHOOSE_SCHEDULE_STEPS} />
                <Note label="Don't see your account?">
                    The account list only shows accounts that match the option you picked: WayaBank accounts for
                    Instant, and other banks for Daily or Weekly. If it says you haven't added one yet, click{' '}
                    <strong>Add a settlement account</strong>, add it, then come back and save.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[4]}>
                <h2 className="wq-h2">From payment to your bank</h2>
                <p className="wq-p">This is what happens to every payment, from your customer to your account.</p>
                <Steps items={PAYMENT_TO_BANK} />

                <h4 className="wq-h4">What you receive</h4>
                <p className="wq-p">
                    WayaQuick's fee comes off each payment before it's settled: 1.5%, capped at ₦2,000, for every
                    payment method —{' '}
                    <Link to="/pricing" className="wq-link">
                        see Pricing
                    </Link>
                    . For example:
                </p>
                <WqTable columns={['', 'Amount']} rows={FEE_EXAMPLE} />
            </section>

            <section className="wq-section" id={CONTENTS[5]}>
                <h2 className="wq-h2">Track your settlements</h2>
                <p className="wq-p">
                    Click <strong>Settlements</strong> in the side menu, under <strong>Payments</strong>.
                </p>
                <Crumbs items={['Payments', 'Settlements']} />

                <h4 className="wq-h4">Summary cards</h4>
                <WqTable columns={['Card', 'What it shows']} rows={STAT_CARDS} />

                <h4 className="wq-h4">Settlement list</h4>
                <p className="wq-p">
                    Every settlement is listed with these details. Click <strong>Filter</strong> to show only one
                    status or a date range, or <strong>Export CSV</strong> to download the list.
                </p>
                <WqTable columns={['Column', 'Meaning']} rows={LIST_COLUMNS} />

                <h4 className="wq-h4">Settlement details and receipt</h4>
                <p className="wq-p">
                    Click the action button on any settlement to see its reference, settlement date, the bank and
                    account it was paid into, and the settlement interval. Click <strong>Download Receipt</strong>{' '}
                    for a PDF receipt you can keep or share with your accountant.
                </p>
            </section>

            <section className="wq-section" id={CONTENTS[6]}>
                <h2 className="wq-h2">Settlement statuses</h2>
                <WqTable columns={['Status', 'What it means']} rows={STATUSES} />
            </section>

            <section className="wq-section" id={CONTENTS[7]}>
                <h2 className="wq-h2">Change your settlement account</h2>
                <p className="wq-p">You can change which account you're settled into, or how often, at any time.</p>
                <Steps items={CHANGE_ACCOUNT_STEPS} />
                <p className="wq-p">
                    To remove an account you no longer use, click <strong>Delete</strong> next to it in the
                    Settlement Account tab.
                </p>
                <Note label="Default account">
                    Your default account can't be deleted. Make another account the default first, then delete the
                    old one. Changes apply to settlements made after you save — money already settled stays where it
                    was paid.
                </Note>
            </section>

            <section className="wq-section" id={CONTENTS[8]}>
                <h2 className="wq-h2">Common questions</h2>

                <h4 className="wq-h4">Can I be settled into more than one account?</h4>
                <p className="wq-p">
                    You can add several settlement accounts, but each settlement goes to your one default account.
                    Switch the default whenever you need to.
                </p>

                <h4 className="wq-h4">Why can't I choose Instant?</h4>
                <p className="wq-p">
                    Instant settlement is only available into a WayaBank account. Add your WayaBank account under
                    Settlement Account, then choose Instant in Payment Preferences.
                </p>

                <h4 className="wq-h4">A payment shows in Transactions, so why hasn't it been settled?</h4>
                <p className="wq-p">
                    It's waiting for its settlement date. On Daily (T+1) it's settled the day after the payment, and
                    on Weekly at the end of the week. Its status on the Settlements page is <strong>Pending</strong>{' '}
                    until then.
                </p>

                <h4 className="wq-h4">A settlement failed or never arrived. What should I do?</h4>
                <p className="wq-p">
                    Check that your settlement account details are correct, then contact us with the settlement's{' '}
                    <strong>Reference ID</strong> through the{' '}
                    <Link to="/complaints" className="wq-link">
                        support page
                    </Link>{' '}
                    or the support chat in your dashboard.
                </p>
            </section>
        </DocPage>
    );
}

export default Settlement;